/**
 * Ubiquiti radio-antenna catalog.
 *
 * Two product families: airFiber (carrier-class long-range PtP with
 * LTU / Wave technology — multi-km links at GbE+ throughput) and
 * airMAX (5 GHz 802.11ac CPE for shorter PtP / PtMP). The 60 GHz
 * AF60-LR sits between them — long-range 60 GHz with built-in
 * high-gain dish.
 *
 * Verified against the official Ubiquiti datasheet PDFs and the
 * techspecs.ui.com pages (Ubiquiti's structured-spec mirror).
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const UBIQUITI_RADIO_ANTENNAS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // airFiber — carrier-class long-range PtP
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://dl.ubnt.com/datasheets/airfiber/airFiber_5XHD_DS.pdf
  // SRC: https://techspecs.ui.com/uisp/wireless/airfiber-5xhd-1
  //      airFiber AF-5XHD: 5 GHz carrier backhaul with LTU tech, 100
  //      MHz channel BW, adaptive 1024QAM (4096QAM via upgrade), 21.2
  //      bps/Hz spectral efficiency, OFDM/OFDMA with 2/4/8 subchannel
  //      scheduling, up to 1.34 Gbps throughput, max range 100 km
  //      (env-dependent). External antenna compatible (airFiber X
  //      antennas 23-34 dBi). Carrier-grade synchronisation + GPS.
  {
    id: 'ubiquiti-af-5xhd',
    manufacturer: 'Ubiquiti',
    model: 'airFiber 5XHD (AF-5XHD)',
    category: 'radio-antenna',
    tagline:
      'airFiber 5XHD — 5 GHz carrier backhaul, 1.34 Gbps, up to 100 km, LTU + 1024QAM',
    verified: true,
    datasheetUrl: 'https://dl.ubnt.com/datasheets/airfiber/airFiber_5XHD_DS.pdf',
    specs: {
      band: '5 GHz',
      rangeM: 100000, // up to 100 km LoS
      gainDbi: 30, // typical mid-range from compatible 23-34 dBi airFiber X
      beamWidthDeg: 5, // airFiber X grid typical
      throughputMbps: 1340,
      standard: 'proprietary', // LTU (Lightning Tracking Unit) — Ubiquiti TDMA, not 802.11
      mimo: '2×2',
      ipRating: 'IP55',
      operatingTempC: '-40..+55',
    },
  },

  // SRC: https://fcc.report/FCC-ID/SWX-AF60LR/4760559.pdf
  // SRC: https://www.balticnetworks.com/products/ubiquiti-airfiber-60-ghz-long-range-radio-system-...
  // SRC: https://www.batna24.com/en/p/ubiquiti-af60lr-radioline-rmnkj
  //      airFiber 60 LR (AF60-LR): 60 GHz Wave Technology, up to 12 km
  //      with integrated high-gain dish, 1.8 Gbps throughput, 43 dBi
  //      antenna gain, 21 dBm max TX power, 1× Gigabit LAN, integrated
  //      Bluetooth radio (setup) + GPS, true-duplex Gigabit performance.
  {
    id: 'ubiquiti-af60-lr',
    manufacturer: 'Ubiquiti',
    model: 'airFiber 60 LR (AF60-LR)',
    category: 'radio-antenna',
    tagline:
      'airFiber 60 LR — 60 GHz long-range dish, 1.8 Gbps, 12 km, 43 dBi, GPS + BT',
    verified: true,
    datasheetUrl: 'https://fcc.report/FCC-ID/SWX-AF60LR/4760559.pdf',
    specs: {
      band: '60 GHz',
      rangeM: 12000,
      gainDbi: 43,
      beamWidthDeg: 2, // narrow 60 GHz dish
      throughputMbps: 1800,
      standard: 'proprietary', // Ubiquiti Wave Technology (60 GHz TDMA)
      mimo: '1×1',
      ipRating: 'IPX5',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // airMAX ac — 5 GHz 802.11ac PtP / PtMP CPE
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://dl.ubnt.com/datasheets/LiteBeam/LiteBeam_AC_Gen2_DS.pdf
  // SRC: https://www.amazon.com/Wireless-LBE-5AC-GEN2-PRE-CONFIGURED-Ubiquiti-Configured/dp/B0DP85WC5Z
  //      LBE-5AC-Gen2 LiteBeam ac Gen2: 5 GHz airMAX ac CPE with
  //      dedicated 2.4 GHz management radio, 23 dBi directional
  //      antenna, 450+ Mbps real throughput, 15+ km PtP range,
  //      1× 10/100/1000 GbE, passive PoE, 7 W max, wind survivability
  //      200 km/h. Best price-per-km in the airMAX line.
  {
    id: 'ubiquiti-lbe-5ac-gen2',
    manufacturer: 'Ubiquiti',
    model: 'LiteBeam 5AC Gen2 (LBE-5AC-Gen2)',
    category: 'radio-antenna',
    tagline:
      'LiteBeam 5AC Gen2 — 5 GHz airMAX ac CPE, 23 dBi, 450 Mbps, 15+ km, 7 W',
    verified: true,
    datasheetUrl: 'https://dl.ubnt.com/datasheets/LiteBeam/LiteBeam_AC_Gen2_DS.pdf',
    specs: {
      band: '5 GHz',
      rangeM: 15000,
      gainDbi: 23,
      beamWidthDeg: 9, // 23 dBi directional, datasheet typical
      throughputMbps: 450,
      standard: '802.11ac',
      mimo: '2×2',
      poeInVoltage: '24 V passive',
      ipRating: 'IPX5',
      operatingTempC: '-40..+70',
      powerConsumptionW: 7,
    },
  },

  // SRC: https://dl.ubnt.com/datasheets/NanoBeam_ac/NanoBeam_AC_Gen2_DS.pdf
  // SRC: https://www.broadbandbuyer.com/products/28788-ubiquiti-nbe-5ac-gen2/
  //      NBE-5AC-Gen2 NanoBeam ac Gen2: 5 GHz airMAX ac CPE with
  //      dedicated management radio, 19 dBi dual-linear-polarisation
  //      antenna, 450+ Mbps throughput, 15+ km range, 8.5 W max,
  //      MIPS 74Kc 720 MHz, 128 MB DDR2. PtP / PtMP capable with
  //      selectable channel widths (10/20/30/40/50/60/80 MHz PtP).
  {
    id: 'ubiquiti-nbe-5ac-gen2',
    manufacturer: 'Ubiquiti',
    model: 'NanoBeam 5AC Gen2 (NBE-5AC-Gen2)',
    category: 'radio-antenna',
    tagline:
      'NanoBeam 5AC Gen2 — 5 GHz airMAX ac CPE, 19 dBi, 450 Mbps, 15+ km, dual-pol',
    verified: true,
    datasheetUrl: 'https://dl.ubnt.com/datasheets/NanoBeam_ac/NanoBeam_AC_Gen2_DS.pdf',
    specs: {
      band: '5 GHz',
      rangeM: 15000,
      gainDbi: 19,
      beamWidthDeg: 13, // 19 dBi panel typical
      throughputMbps: 450,
      standard: '802.11ac',
      mimo: '2×2',
      poeInVoltage: '24 V passive',
      ipRating: 'IPX5',
      operatingTempC: '-40..+70',
      powerConsumptionW: 8.5,
    },
  },

  // SRC: https://dl.ubnt.com/datasheets/PowerBeam_ac/PowerBeam5ac_DS.pdf
  // SRC: https://store.ui.com/us/en/products/pbe-5ac-500
  // SRC: https://flyteccomputers.com/ubiquiti-pbe-5ac-500
  //      PBE-5AC-500 PowerBeam ac: 5 GHz airMAX ac bridge, 500 mm dish
  //      27 dBi gain, 450+ Mbps real TCP/IP throughput, 25+ km range,
  //      5150-5875 MHz worldwide / 5725-5850 USA, 8.5 W max, MIPS 74Kc
  //      560 MHz, 128 MB DDR2, GbE port, 200 km/h wind survivability,
  //      protective radome.
  {
    id: 'ubiquiti-pbe-5ac-500',
    manufacturer: 'Ubiquiti',
    model: 'PowerBeam 5AC 500 (PBE-5AC-500)',
    category: 'radio-antenna',
    tagline:
      'PowerBeam 5AC 500 — 5 GHz 500 mm dish, 27 dBi, 450 Mbps, 25+ km, radome',
    verified: true,
    datasheetUrl: 'https://dl.ubnt.com/datasheets/PowerBeam_ac/PowerBeam5ac_DS.pdf',
    specs: {
      band: '5 GHz',
      rangeM: 25000,
      gainDbi: 27,
      beamWidthDeg: 6, // 500 mm 27 dBi dish typical
      throughputMbps: 450,
      standard: '802.11ac',
      mimo: '2×2',
      poeInVoltage: '24 V passive',
      ipRating: 'IPX5',
      operatingTempC: '-40..+70',
      powerConsumptionW: 8.5,
    },
  },
];
